import React, { useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, Bell, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useInstructorPendingRequestsCount, useInstructorRequests, useApproveClassRequest, useDenyClassRequest } from '@/hooks/useClassRequests';
import { RequestManagementSection } from '@/components/classRequests/RequestManagementSection';
import { AgendaConfigurationModal } from '@/components/calendar/AgendaConfigurationModal';

const RequestForClassesSection: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isAgendaModalOpen, setIsAgendaModalOpen] = useState(false);

  // Get pending requests count and data
  const { data: pendingCount = 0, isLoading: countLoading } = useInstructorPendingRequestsCount(user?.id || '');
  const { data: requests = [], isLoading: requestsLoading } = useInstructorRequests(user?.id || '');
  
  // Mutation hooks
  const approveMutation = useApproveClassRequest();
  const denyMutation = useDenyClassRequest();

  const handleSetUpSchedule = () => {
    setIsAgendaModalOpen(true);
  };

  const handleAgendaConfigSaved = () => {
    setIsAgendaModalOpen(false);
    // Refresh requests data after config is saved
    // The queries will automatically refetch
  };

  const handleSeeAll = () => {
    navigate(`/instructor-slots/${user?.id}`);
  };

  const handleApprove = async (requestId: string) => {
    await approveMutation.mutateAsync({ requestId, instructorId: user?.id || '' });
  };

  const handleDeny = async (requestId: string) => {
    await denyMutation.mutateAsync({ requestId, instructorId: user?.id || '' });
  };

  const handleViewProfile = (studentId: string) => {
    navigate(`/profile/${studentId}`);
  };

  // If no requests, show setup CTA
  if (pendingCount === 0 && !countLoading) {
    return (
      <>
        <div className="mb-8">
          <div 
            className="bg-[#2D2424] rounded-lg p-4 cursor-pointer hover:bg-[#3D3434] transition-colors"
            onClick={handleSetUpSchedule}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSetUpSchedule();
              }
            }}
            aria-label="Set up your schedule to attract more students"
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Users className="w-6 h-6 text-[#D72E16]" />
              </div>
              
              <div className="flex-1">
                <h3 className="text-sm font-bold text-white font-roboto mb-1">
                  {t('request_for_classes.title', 'Request for classes')}
                </h3>
                <p className="text-xs text-gray-300 font-roboto mb-3">
                  {t('request_for_classes.subtitle', 'Share your free spots for classes. Make your schedule public and attract more students!')}
                </p>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSetUpSchedule();
                  }}
                  className="inline-flex items-center space-x-2 bg-[#D72E16] hover:bg-[#B91C1C] text-white px-4 py-2 rounded-full text-xs font-medium transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  <span>{t('request_for_classes.setup_schedule', 'Set Up my Schedule')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Agenda Configuration Modal */}
        <AgendaConfigurationModal
          isOpen={isAgendaModalOpen}
          onClose={() => setIsAgendaModalOpen(false)}
          onConfigSaved={handleAgendaConfigSaved}
        />
      </>
    );
  }

  // If has requests, show management section
  return (
    <>
      <RequestManagementSection
        instructorId={user?.id || ''}
        requests={requests}
        pendingCount={pendingCount}
        isLoading={countLoading || requestsLoading}
        onApprove={handleApprove}
        onDeny={handleDeny}
        onViewProfile={handleViewProfile}
      />
      
      {/* Agenda Configuration Modal */}
      <AgendaConfigurationModal
        isOpen={isAgendaModalOpen}
        onClose={() => setIsAgendaModalOpen(false)}
        onConfigSaved={handleAgendaConfigSaved}
      />
    </>
  );
};

export default RequestForClassesSection;



